import { Typography } from '@app/components';
import { CustomerEmailHeaderOutDto } from '@app/swagger-types';
import { format } from 'date-fns';
import { FORMAT } from '@app/constants/formats';
import { ReasonPromptDialog } from './ReasonPromptDialog';
import { EmailCard } from '../../shared/EmailCard';
import { StatusPill, StatusPillVariant } from '../../shared/StatusPill';

// admin-emailbeheer blok A stap 5, opdracht §5 "Kop". Every field here is live-resolved by the
// backend at request time (track, business-domain, timezone) - never cached, see
// EmailCustomerAdminFacadeImpl's own javadoc for why.
export const CustomerHeaderCard: React.FC<{
  header: CustomerEmailHeaderOutDto;
  onUnblock: (reason: string) => void;
  unblockLoading?: boolean;
}> = ({ header, onUnblock, unblockLoading }) => {
  const suppression = header.suppression;

  return (
    <EmailCard>
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <Typography variant="l" className="font-bold text-gray-800">
            {header.fullName || header.email}
          </Typography>
          <Typography variant="xs" className="text-gray-500">
            {header.email}
          </Typography>
        </div>
        <div className="flex items-center gap-2">
          <SuppressionBadge suppression={suppression} />
          {suppression?.suppressed && (
            <ReasonPromptDialog
              title="Unblock this address?"
              text="Only for a demonstrably wrongful bounce. Removes the address at SendGrid too - if that fails, nothing is removed here either."
              actionLabel="Unblock"
              loading={unblockLoading}
              onConfirm={onUnblock}
              trigger={
                <button className="rounded border border-[#fde2e2] px-2 py-1 text-xs font-bold text-[#b42d2d] hover:bg-[#fde2e2]">
                  unblock
                </button>
              }
            />
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat label="Plan">
          {header.planType} / {header.customerGen}
        </Stat>
        <Stat label="Trial day">{header.trialDay ?? '-'}</Stat>
        <Stat label="Track">
          {header.track ?? '-'}
          <span className="ml-1 text-xs font-normal text-gray-400">
            ({header.trackCodeCount ?? 0} codes, {header.trackScanCount ?? 0} scans)
          </span>
        </Stat>
        <Stat label="Business domain">{header.businessDomain ? 'yes' : 'no'}</Stat>
        <Stat label="Language">{header.language ?? '-'}</Stat>
        <Stat label="Time zone">{header.timeZone ?? '-'}</Stat>
      </div>

      {!!header.journeys?.length && (
        <div className="mt-4 flex flex-col gap-1">
          <Typography variant="xs" className="font-bold text-gray-500">
            Journeys
          </Typography>
          {header.journeys.map((journey, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-mono">{journey.journeyKey}</span>
              <StateBadge state={journey.state} />
              {journey.stopReason && (
                <span className="text-gray-500">
                  stop_reason={journey.stopReason}
                  {journey.stoppedAt && ` (${format(new Date(journey.stoppedAt), FORMAT.FULL_TIME)})`}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </EmailCard>
  );
};

const Stat: React.FC<{ label: string }> = ({ label, children }) => (
  <div>
    <Typography variant="xxs" className="text-gray-400">
      {label}
    </Typography>
    <Typography variant="s" className="font-bold text-gray-800">
      {children}
    </Typography>
  </div>
);

const STATE_VARIANT: Record<string, StatusPillVariant> = {
  ACTIVE: 'ok',
  STOPPED: 'warn',
  COMPLETED: 'mut',
};

const StateBadge: React.FC<{ state?: string }> = ({ state }) =>
  state ? <StatusPill variant={STATE_VARIANT[state] ?? 'mut'}>{state}</StatusPill> : null;

const SuppressionBadge: React.FC<{ suppression?: CustomerEmailHeaderOutDto['suppression'] }> = ({ suppression }) => {
  if (!suppression?.suppressed) {
    return <StatusPill variant="ok">not suppressed</StatusPill>;
  }

  const where = suppression.confirmedAtSendGrid ? 'us + SendGrid' : 'us only';

  return (
    <StatusPill variant="bad">
      suppressed ({suppression.reason}) - {where}
    </StatusPill>
  );
};
